(() => {
  const ELEMENT_NAME = 'infinite-scroll-gallery';
  const bodyScrollLocks = new Set();

  if (customElements.get(ELEMENT_NAME)) return;

  class InfiniteScrollGallery extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;

      this._connected = true;
      this._built = false;
      this._events = new AbortController();
      this._lifecycleId = (this._lifecycleId || 0) + 1;

      this._readElements();
      this._readConfig();
      this._resetState();

      if (!this._textTrack || !this._mediaTrack) {
        this.dataset.galleryState = 'error';
        return;
      }

      this._moveModalToBody();
      this._bindEvents();
      this._observeSection();
      this.dataset.galleryState = 'idle';
    }

    disconnectedCallback() {
      this.destroy();
    }

    _readElements() {
      this._textWrapper = this.querySelector('[data-gallery-text-wrapper]');
      this._textTrack = this.querySelector('[data-gallery-text-track]');
      this._mediaWrapper = this.querySelector('[data-gallery-media-wrapper]');
      this._mediaTrack = this.querySelector('[data-gallery-media-track]');
      this._configElement = this.querySelector('[data-gallery-config]');
      this._modal = this.querySelector('[data-gallery-modal]');
      this._modalClose = this._modal?.querySelector('[data-gallery-modal-close]');
      this._previewMedia = this._modal?.querySelector('[data-gallery-preview-media]');
    }

    _readConfig() {
      let config = {};

      try {
        config = JSON.parse(this._configElement?.textContent || '{}');
      } catch (error) {
        console.error('Infinite gallery config could not be parsed.', error);
      }

      this._textItems = Array.isArray(config.text)
        ? config.text.filter((item) => typeof item === 'string' && item.trim() !== '')
        : [];

      this._mediaItems = Array.isArray(config.media)
        ? config.media.map((item) => this._normalizeMediaItem(item)).filter(Boolean)
        : [];

      const settings = config.settings || {};
      this._baseTextSpeed = -this._clamp(Math.abs(Number(settings.textSpeed) || 1), 0.1, 5);
      this._baseMediaSpeed = this._clamp(Math.abs(Number(settings.imageSpeed) || 1), 0.1, 5);
      this._scrollBoost = this._clamp(Number(settings.scrollBoost) || 0, 0, 10);
      this._dragEnabled = settings.dragEnabled !== false;
      this.dataset.dragEnabled = String(this._dragEnabled);
    }

    _normalizeMediaItem(item) {
      if (!item || typeof item !== 'object') return null;

      if (item.type === 'image' && item.src) {
        return {
          type: 'image',
          src: String(item.src),
          previewSrc: item.previewSrc ? String(item.previewSrc) : String(item.src),
          srcSet: item.srcSet ? String(item.srcSet) : '',
          alt: item.alt ? String(item.alt) : 'Gallery image'
        };
      }

      if (item.type === 'video') {
        const sources = Array.isArray(item.sources)
          ? item.sources
              .filter((source) => source?.src)
              .map((source) => ({
                src: String(source.src),
                type: source.type ? String(source.type) : ''
              }))
          : [];

        if (sources.length === 0) return null;

        return {
          type: 'video',
          sources,
          poster: item.poster ? String(item.poster) : '',
          posterSrcSet: item.posterSrcSet ? String(item.posterSrcSet) : '',
          alt: item.alt ? String(item.alt) : 'Play video'
        };
      }

      if (item.type === 'tiktok' && item.videoId) {
        return {
          type: 'tiktok',
          videoId: String(item.videoId).trim(),
          username: item.username ? String(item.username) : ''
        };
      }

      return null;
    }

    _resetState() {
      this._textX = 0;
      this._mediaX = 0;
      this._textWidth = 0;
      this._mediaWidth = 0;
      this._textRepeats = 1;
      this._mediaRepeats = 1;
      this._textSpeed = this._baseTextSpeed;
      this._mediaSpeed = this._baseMediaSpeed;
      this._scrollImpulse = 0;
      this._lastFrameTime = 0;
      this._lastScrollY = window.scrollY;
      this._isInViewport = false;
      this._modalOpen = false;
      this._restoreTriggerFocus = false;
      this._hasTrackFocus = false;
      this._dragState = null;
      this._isDragging = false;
      this._suppressClickUntil = 0;
      this._rafId = 0;
      this._resizeRafId = 0;
      this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._reducedMotion = this._prefersReducedMotion.matches;
    }

    _moveModalToBody() {
      if (!this._modal || this._modal.parentElement === document.body) return;

      this._modalPlaceholder = document.createComment('infinite-gallery-modal');
      this._modal.before(this._modalPlaceholder);
      document.body.appendChild(this._modal);
    }

    _restoreModal() {
      if (!this._modal || !this._modalPlaceholder?.parentNode) return;

      this._modalPlaceholder.parentNode.insertBefore(this._modal, this._modalPlaceholder);
      this._modalPlaceholder.remove();
      this._modalPlaceholder = null;
    }

    _bindEvents() {
      const signal = this._events.signal;

      window.addEventListener('resize', () => this._scheduleMeasure(), { passive: true, signal });
      window.addEventListener('scroll', () => this._handlePageScroll(), { passive: true, signal });
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) this._cancelPointerDrag();
          this._updateAnimationState();
        },
        { signal }
      );
      document.addEventListener('keydown', (event) => this._handleDocumentKeydown(event), { signal });
      window.addEventListener('blur', () => this._cancelPointerDrag(), { signal });
      window.addEventListener('pointermove', (event) => this._handlePointerMove(event), {
        passive: false,
        signal
      });
      window.addEventListener('pointerup', (event) => this._endPointerDrag(event), { signal });
      window.addEventListener('pointercancel', (event) => this._endPointerDrag(event), { signal });

      this._prefersReducedMotion.addEventListener(
        'change',
        (event) => {
          this._reducedMotion = event.matches;
          this._updateAnimationState();
        },
        { signal }
      );

      this._mediaTrack.addEventListener('click', (event) => this._handleMediaClick(event), { signal });
      this._mediaTrack.addEventListener('keydown', (event) => this._handleMediaKeydown(event), { signal });
      this._mediaTrack.addEventListener(
        'focusin',
        (event) => {
          this._hasTrackFocus = true;
          this._updateAnimationState();
          const card = event.target.closest('[data-media-index]');
          if (card) this._revealFocusedCard(card);
        },
        { signal }
      );
      this._mediaTrack.addEventListener(
        'focusout',
        (event) => {
          if (!this._mediaTrack.contains(event.relatedTarget)) {
            this._hasTrackFocus = false;
            this._updateAnimationState();
          }
        },
        { signal }
      );

      if (this._dragEnabled) {
        this._textWrapper?.addEventListener(
          'pointerdown',
          (event) => this._startPointerDrag(event, 'text'),
          { signal }
        );
        this._textWrapper?.addEventListener('lostpointercapture', () => this._cancelPointerDrag(), {
          signal
        });
        this._mediaWrapper?.addEventListener(
          'pointerdown',
          (event) => this._startPointerDrag(event, 'media'),
          { signal }
        );
        this._mediaWrapper?.addEventListener('lostpointercapture', () => this._cancelPointerDrag(), {
          signal
        });
      }

      this._modalClose?.addEventListener('click', () => this.closePreview(), { signal });
      this._modal?.addEventListener(
        'click',
        (event) => {
          if (event.target === this._modal) this.closePreview();
        },
        { signal }
      );
      this._modal?.addEventListener('keydown', (event) => this._trapModalFocus(event), { signal });
    }

    _observeSection() {
      if (!('IntersectionObserver' in window)) {
        this._isInViewport = true;
        this._build();
        return;
      }

      const lifecycleId = this._lifecycleId;
      const buildObserver = new IntersectionObserver(
        (entries) => {
          if (!this._connected || this._lifecycleId !== lifecycleId) return;
          if (!entries[0]?.isIntersecting) return;
          buildObserver.disconnect();
          this._build();
        },
        { rootMargin: '800px 0px' }
      );

      const visibilityObserver = new IntersectionObserver(
        (entries) => {
          if (!this._connected || this._lifecycleId !== lifecycleId) return;
          this._isInViewport = Boolean(entries[0]?.isIntersecting);
          this._updateAnimationState();
        },
        { rootMargin: '120px 0px', threshold: 0.01 }
      );

      this._buildObserver = buildObserver;
      this._visibilityObserver = visibilityObserver;
      buildObserver.observe(this);
      visibilityObserver.observe(this);
    }

    _build() {
      if (this._built || !this._connected) return;

      this._built = true;
      this._renderTextTrack();
      this._renderMediaTrack();
      this.dataset.galleryState = 'ready';

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this._scheduleMeasure());
        if (this._textWrapper) this._resizeObserver.observe(this._textWrapper);
        if (this._mediaWrapper) this._resizeObserver.observe(this._mediaWrapper);
      }

      const lifecycleId = this._lifecycleId;
      document.fonts?.ready.then(() => {
        if (this._connected && this._lifecycleId === lifecycleId) this._scheduleMeasure();
      });

      requestAnimationFrame(() => {
        if (!this._connected || this._lifecycleId !== lifecycleId) return;
        this._measureTracks(false);
        this._updateAnimationState();
      });
    }

    _renderTextTrack() {
      this._textTrack.replaceChildren();

      if (this._textItems.length === 0) {
        if (this._textWrapper) this._textWrapper.hidden = true;
        return;
      }

      if (this._textWrapper) {
        this._textWrapper.hidden = false;
        this._textWrapper.setAttribute('aria-label', this._textItems.join(' '));
      }

      const fragment = document.createDocumentFragment();

      for (let copyIndex = 0; copyIndex < 2; copyIndex += 1) {
        const set = document.createElement('div');
        set.className = 'text-scroll__set';
        set.dataset.gallerySet = '';
        set.setAttribute('aria-hidden', 'true');

        for (let repeatIndex = 0; repeatIndex < this._textRepeats; repeatIndex += 1) {
          this._textItems.forEach((text) => {
            const item = document.createElement('span');
            item.className = 'text-item';
            item.textContent = text;
            set.appendChild(item);
          });
        }

        fragment.appendChild(set);
      }

      this._textTrack.appendChild(fragment);
    }

    _renderMediaTrack() {
      this._mediaTrack.replaceChildren();

      if (this._mediaItems.length === 0) {
        if (this._mediaWrapper) this._mediaWrapper.hidden = true;
        return;
      }

      if (this._mediaWrapper) this._mediaWrapper.hidden = false;
      const fragment = document.createDocumentFragment();

      for (let copyIndex = 0; copyIndex < 2; copyIndex += 1) {
        const set = document.createElement('div');
        set.className = 'image-scroll__set';
        set.dataset.gallerySet = '';
        const accessibleCopy = copyIndex === 1;
        if (!accessibleCopy) set.setAttribute('aria-hidden', 'true');

        for (let repeatIndex = 0; repeatIndex < this._mediaRepeats; repeatIndex += 1) {
          this._mediaItems.forEach((item, mediaIndex) => {
            const isAccessible = accessibleCopy && repeatIndex === 0;
            set.appendChild(
              this._createMediaCard(item, mediaIndex, isAccessible, copyIndex, repeatIndex)
            );
          });
        }

        fragment.appendChild(set);
      }

      this._mediaTrack.appendChild(fragment);
    }

    _createMediaCard(item, mediaIndex, isAccessible, copyIndex, repeatIndex) {
      const card = document.createElement('div');
      card.className = 'image-item has-link';
      card.dataset.mediaIndex = String(mediaIndex);
      card.dataset.mediaType = item.type;
      card.dataset.galleryCopy = String(copyIndex);
      card.dataset.galleryRepeat = String(repeatIndex);
      if (isAccessible) {
        card.setAttribute('tabindex', '0');
        card.setAttribute('role', 'button');
        card.setAttribute('aria-label', this._mediaLabel(item));
      } else {
        card.setAttribute('aria-hidden', 'true');
      }

      if (item.type === 'image') {
        const image = this._createImage(item.src, item.alt, item.srcSet);
        card.appendChild(image);
        return card;
      }

      if (item.type === 'video') {
        if (item.poster) {
          const poster = this._createImage(item.poster, '', item.posterSrcSet);
          poster.classList.add('gallery-video-poster');
          card.appendChild(poster);
        } else {
          card.appendChild(this._createPlaceholder('Video'));
        }

        const playIndicator = document.createElement('span');
        playIndicator.className = 'gallery-play-indicator';
        playIndicator.setAttribute('aria-hidden', 'true');
        card.appendChild(playIndicator);
        return card;
      }

      const placeholder = this._createPlaceholder(item.username || 'TikTok');
      placeholder.classList.add('gallery-tiktok-placeholder');
      card.appendChild(placeholder);
      return card;
    }

    _createImage(src, alt, srcSet = '') {
      const image = document.createElement('img');
      image.alt = alt;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      image.fetchPriority = 'low';
      image.sizes = '(max-width: 480px) 240px, (max-width: 768px) 270px, (max-width: 1024px) 225px, 300px';
      if (srcSet) image.srcset = srcSet;
      image.src = src;
      return image;
    }

    _createPlaceholder(label) {
      const placeholder = document.createElement('span');
      placeholder.className = 'gallery-media-placeholder';

      const icon = document.createElement('span');
      icon.className = 'gallery-media-placeholder__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '\u266A';

      const text = document.createElement('span');
      text.className = 'gallery-media-placeholder__label';
      text.textContent = label;

      placeholder.append(icon, text);
      return placeholder;
    }

    _mediaLabel(item) {
      if (item.type === 'image') return `View ${item.alt || 'gallery image'}`;
      if (item.type === 'video') return item.alt || 'Play video';
      return item.username ? `Open TikTok video from ${item.username}` : 'Open TikTok video';
    }

    _ensureCoverage() {
      let rerendered = false;

      const textSet = this._textTrack.querySelector('[data-gallery-set]');
      const textSetWidth = textSet?.offsetWidth || 0;
      const textBaseWidth = textSetWidth / this._textRepeats;
      if (textBaseWidth > 0 && this._textWrapper) {
        const needed = Math.max(1, Math.ceil(this._coverageTarget(this._textWrapper) / textBaseWidth));
        if (needed !== this._textRepeats) {
          this._textRepeats = needed;
          this._renderTextTrack();
          rerendered = true;
        }
      }

      const mediaSet = this._mediaTrack.querySelector('[data-gallery-set]');
      const mediaSetWidth = mediaSet?.offsetWidth || 0;
      const mediaBaseWidth = mediaSetWidth / this._mediaRepeats;
      if (mediaBaseWidth > 0 && this._mediaWrapper) {
        const needed = Math.max(1, Math.ceil(this._coverageTarget(this._mediaWrapper) / mediaBaseWidth));
        if (needed !== this._mediaRepeats) {
          this._mediaRepeats = needed;
          this._renderMediaTrack();
          rerendered = true;
        }
      }

      return rerendered;
    }

    _coverageTarget(wrapper) {
      const gap = Number.parseFloat(getComputedStyle(this).getPropertyValue('--gallery-gap')) || 40;
      return wrapper.clientWidth + wrapper.clientHeight * 0.04 + gap;
    }

    _measureTracks(preservePosition = true) {
      if (!this._built) return;

      const oldTextWidth = this._textWidth;
      const oldMediaWidth = this._mediaWidth;
      const oldTextSequenceWidth = oldTextWidth / this._textRepeats;
      const oldMediaSequenceWidth = oldMediaWidth / this._mediaRepeats;
      const oldTextPhase = this._sequencePhase(this._textX, oldTextSequenceWidth);
      const oldMediaPhase = this._sequencePhase(this._mediaX, oldMediaSequenceWidth);

      this._ensureCoverage();

      this._textWidth = this._textTrack.querySelector('[data-gallery-set]')?.offsetWidth || 0;
      this._mediaWidth = this._mediaTrack.querySelector('[data-gallery-set]')?.offsetWidth || 0;
      const newTextSequenceWidth = this._textWidth / this._textRepeats;
      const newMediaSequenceWidth = this._mediaWidth / this._mediaRepeats;

      this._textX = preservePosition && oldTextWidth
        ? -this._textWidth + newTextSequenceWidth * oldTextPhase
        : -this._textWidth;
      this._mediaX = preservePosition && oldMediaWidth
        ? -this._mediaWidth + newMediaSequenceWidth * oldMediaPhase
        : -this._mediaWidth;

      this._applyTransforms();
    }

    _sequencePhase(position, sequenceWidth) {
      if (!sequenceWidth) return 0;
      let remainder = position % sequenceWidth;
      if (remainder < 0) remainder += sequenceWidth;
      return remainder / sequenceWidth;
    }

    _scheduleMeasure() {
      if (!this._built || this._resizeRafId) return;

      this._resizeRafId = requestAnimationFrame(() => {
        this._resizeRafId = 0;
        this._measureTracks(true);
      });
    }

    _handlePageScroll() {
      const currentY = window.scrollY;
      const delta = Math.abs(currentY - this._lastScrollY);
      this._lastScrollY = currentY;

      if (
        !this._built ||
        !this._isInViewport ||
        this._scrollBoost <= 0 ||
        this._modalOpen ||
        this._hasTrackFocus ||
        this._reducedMotion ||
        document.hidden
      ) return;
      this._scrollImpulse = Math.min(12, this._scrollImpulse + (delta * this._scrollBoost) / 80);
    }

    _startPointerDrag(event, track) {
      if (!this._dragEnabled || !event.isPrimary) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this._dragState = {
        pointerId: event.pointerId,
        track,
        wrapper: event.currentTarget,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: event.timeStamp,
        axis: null
      };
    }

    _handlePointerMove(event) {
      const state = this._dragState;
      if (!state || state.pointerId !== event.pointerId) return;

      const totalX = event.clientX - state.startX;
      const totalY = event.clientY - state.startY;

      if (!state.axis && Math.max(Math.abs(totalX), Math.abs(totalY)) >= 10) {
        if (Math.abs(totalY) > Math.abs(totalX)) {
          state.axis = 'vertical';
          return;
        }

        state.axis = 'horizontal';
        this._isDragging = true;
        if (this._mediaTrack.contains(document.activeElement)) document.activeElement.blur();
        state.wrapper.setPointerCapture?.(event.pointerId);
        this._updateAnimationState();
      }

      if (state.axis !== 'horizontal') return;

      event.preventDefault();
      const deltaX = event.clientX - state.lastX;
      const elapsed = Math.max(event.timeStamp - state.lastTime, 8);
      const frameVelocity = this._clamp(deltaX / (elapsed / 16.667), -25, 25);

      if (state.track === 'text') {
        this._textX = this._wrapPosition(this._textX + deltaX, this._textWidth);
        this._textSpeed = frameVelocity;
      } else {
        this._mediaX = this._wrapPosition(this._mediaX + deltaX, this._mediaWidth);
        this._mediaSpeed = frameVelocity;
      }

      state.lastX = event.clientX;
      state.lastTime = event.timeStamp;
      this._applyTransforms();
    }

    _endPointerDrag(event) {
      const state = this._dragState;
      if (!state || state.pointerId !== event.pointerId) return;

      this._dragState = null;
      this._isDragging = false;
      if (state.axis === 'horizontal' || state.axis === 'vertical') {
        this._suppressClickUntil = performance.now() + 300;
      }
      if (state.axis === 'horizontal') {
        if (state.wrapper.hasPointerCapture?.(event.pointerId)) {
          state.wrapper.releasePointerCapture(event.pointerId);
        }
      }

      this._updateAnimationState();
    }

    _cancelPointerDrag() {
      const state = this._dragState;
      if (!state) return;

      if (state.axis === 'horizontal' || state.axis === 'vertical') {
        this._suppressClickUntil = performance.now() + 300;
      }
      this._dragState = null;
      this._isDragging = false;
      if (state.wrapper.hasPointerCapture?.(state.pointerId)) {
        state.wrapper.releasePointerCapture(state.pointerId);
      }

      this._updateAnimationState();
    }

    _handleMediaClick(event) {
      if (performance.now() < this._suppressClickUntil) {
        event.preventDefault();
        return;
      }

      const card = event.target.closest('[data-media-index]');
      if (!card || !this._mediaTrack.contains(card)) return;
      this.openPreview(this._mediaItems[Number(card.dataset.mediaIndex)], card, event.detail === 0);
    }

    _handleMediaKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const card = event.target.closest('[data-media-index]');
      if (!card) return;
      event.preventDefault();
      this.openPreview(this._mediaItems[Number(card.dataset.mediaIndex)], card, true);
    }

    _revealFocusedCard(card) {
      if (!this._mediaWrapper || !this._mediaWidth) return;

      const wrapperRect = this._mediaWrapper.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const safeInset = 20;
      let shift = 0;

      if (cardRect.left < wrapperRect.left + safeInset) {
        shift = wrapperRect.left + safeInset - cardRect.left;
      } else if (cardRect.right > wrapperRect.right - safeInset) {
        shift = wrapperRect.right - safeInset - cardRect.right;
      }

      if (shift) {
        // Keep the focused, accessible copy on screen. Normalization resumes after focus leaves.
        this._mediaX += shift;
        this._applyTransforms();
      }
    }

    _handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this._modalOpen) this.closePreview();
    }

    _trapModalFocus(event) {
      if (event.key !== 'Tab' || !this._modalOpen || !this._modal) return;

      const focusable = Array.from(
        this._modal.querySelectorAll(
          'button:not([disabled]), a[href], video[controls], [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === first || !this._modal.contains(activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (activeElement === last || !this._modal.contains(activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    openPreview(item, trigger, restoreTriggerFocus = false) {
      if (!item || !this._modal || !this._previewMedia) return;

      this._lastPreviewTrigger = trigger;
      this._restoreTriggerFocus = restoreTriggerFocus;
      this._releasePreviewMedia();
      this._modalOpen = true;
      this._lockBodyScroll();
      this._modal.classList.add('active');
      this._modal.setAttribute('aria-hidden', 'false');

      if (item.type === 'image') {
        const image = document.createElement('img');
        image.src = item.previewSrc || item.src;
        image.alt = item.alt || 'Gallery image';
        image.decoding = 'async';
        image.fetchPriority = 'high';
        this._previewMedia.appendChild(image);
      } else if (item.type === 'video') {
        const video = document.createElement('video');
        video.controls = true;
        video.autoplay = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'metadata';
        if (item.poster) video.poster = item.poster;

        item.sources.forEach((sourceData) => {
          const source = document.createElement('source');
          source.src = sourceData.src;
          if (sourceData.type) source.type = sourceData.type;
          video.appendChild(source);
        });

        this._previewMedia.appendChild(video);
        video.load();
        video.play().catch(() => {});
      } else if (item.type === 'tiktok') {
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.tiktok.com/embed/v2/${encodeURIComponent(item.videoId)}`;
        iframe.title = item.username ? `TikTok video from ${item.username}` : 'TikTok video';
        iframe.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.tabIndex = -1;
        this._previewMedia.appendChild(iframe);
      }

      this._updateAnimationState();
      const lifecycleId = this._lifecycleId;
      requestAnimationFrame(() => {
        if (this._connected && this._modalOpen && this._lifecycleId === lifecycleId) {
          this._modalClose?.focus();
        }
      });
    }

    closePreview() {
      if (!this._modalOpen) return;

      this._modalOpen = false;
      this._modal?.classList.remove('active');
      this._modal?.setAttribute('aria-hidden', 'true');
      this._releasePreviewMedia();

      this._unlockBodyScroll();

      let trigger = this._lastPreviewTrigger;
      const restoreTriggerFocus = this._restoreTriggerFocus;
      this._lastPreviewTrigger = null;
      this._restoreTriggerFocus = false;
      if (restoreTriggerFocus && trigger?.tabIndex < 0 && trigger.dataset.mediaIndex) {
        const accessibleTrigger = this._mediaTrack?.querySelector(
          `[data-media-index="${trigger.dataset.mediaIndex}"][tabindex="0"]`
        );
        if (accessibleTrigger) {
          const copyIndex = Number(trigger.dataset.galleryCopy);
          const repeatIndex = Number(trigger.dataset.galleryRepeat);
          const sequenceWidth = this._mediaWidth / this._mediaRepeats;
          this._mediaX += (copyIndex - 1) * this._mediaWidth + repeatIndex * sequenceWidth;
          this._applyTransforms();
          trigger = accessibleTrigger;
        }
      }
      if (restoreTriggerFocus && trigger?.isConnected) {
        trigger.focus({ preventScroll: true });
      } else if (this._connected) {
        this.focus({ preventScroll: true });
      }
      this._updateAnimationState();
    }

    _lockBodyScroll() {
      bodyScrollLocks.add(this);
      document.body?.classList.add('infinite-gallery-preview-open');
    }

    _unlockBodyScroll() {
      bodyScrollLocks.delete(this);
      if (bodyScrollLocks.size === 0) {
        document.body?.classList.remove('infinite-gallery-preview-open');
      }
    }

    _releasePreviewMedia() {
      if (!this._previewMedia) return;

      this._previewMedia.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        video.load();
      });

      this._previewMedia.querySelectorAll('iframe').forEach((iframe) => {
        iframe.src = 'about:blank';
      });

      this._previewMedia.replaceChildren();
    }

    _shouldAnimate() {
      return (
        this._connected &&
        this._built &&
        this._isInViewport &&
        !document.hidden &&
        !this._modalOpen &&
        !this._isDragging &&
        !this._hasTrackFocus &&
        !this._reducedMotion &&
        (this._textWidth > 0 || this._mediaWidth > 0)
      );
    }

    _updateAnimationState() {
      if (this._shouldAnimate()) {
        if (!this._rafId) {
          this.classList.add('is-running');
          this._lastFrameTime = 0;
          this._rafId = requestAnimationFrame((time) => this._animate(time));
        }
        return;
      }

      this._stopAnimation();
    }

    _animate(time) {
      if (!this._shouldAnimate()) {
        this._stopAnimation();
        return;
      }

      const frameScale = this._lastFrameTime
        ? this._clamp((time - this._lastFrameTime) / 16.667, 0.25, 2)
        : 1;
      this._lastFrameTime = time;

      const textTarget = this._baseTextSpeed - this._scrollImpulse;
      const mediaTarget = this._baseMediaSpeed + this._scrollImpulse;
      const easing = Math.min(1, 0.035 * frameScale);
      this._textSpeed += (textTarget - this._textSpeed) * easing;
      this._mediaSpeed += (mediaTarget - this._mediaSpeed) * easing;

      this._textX = this._wrapPosition(this._textX + this._textSpeed * frameScale, this._textWidth);
      this._mediaX = this._wrapPosition(this._mediaX + this._mediaSpeed * frameScale, this._mediaWidth);
      this._scrollImpulse *= Math.pow(0.88, frameScale);
      if (this._scrollImpulse < 0.01) this._scrollImpulse = 0;

      this._applyTransforms();
      this._rafId = requestAnimationFrame((nextTime) => this._animate(nextTime));
    }

    _applyTransforms() {
      if (this._textWidth > 0) {
        this._textTrack.style.transform = `translate3d(${this._textX.toFixed(3)}px, -50%, 0)`;
      }
      if (this._mediaWidth > 0) {
        this._mediaTrack.style.transform = `translate3d(${this._mediaX.toFixed(3)}px, -50%, 0)`;
      }
    }

    _wrapPosition(value, width) {
      if (!width) return 0;
      let wrapped = value % width;
      if (wrapped >= 0) wrapped -= width;
      return wrapped;
    }

    _stopAnimation() {
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this._rafId = 0;
      this._lastFrameTime = 0;
      this._scrollImpulse = 0;
      this._textSpeed = this._baseTextSpeed;
      this._mediaSpeed = this._baseMediaSpeed;
      this.classList.remove('is-running');
    }

    _clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    destroy() {
      if (!this._connected) return;

      this._connected = false;
      this._lifecycleId += 1;
      this._stopAnimation();
      if (this._resizeRafId) cancelAnimationFrame(this._resizeRafId);
      this._resizeRafId = 0;
      this._buildObserver?.disconnect();
      this._visibilityObserver?.disconnect();
      this._resizeObserver?.disconnect();
      this._events?.abort();

      if (this._dragState?.wrapper?.hasPointerCapture?.(this._dragState.pointerId)) {
        this._dragState.wrapper.releasePointerCapture(this._dragState.pointerId);
      }
      this._dragState = null;
      this._isDragging = false;

      if (this._modalOpen) this.closePreview();
      else {
        this._unlockBodyScroll();
        this._releasePreviewMedia();
      }
      this._restoreModal();

      this._textTrack?.replaceChildren();
      this._mediaTrack?.replaceChildren();
      this._built = false;
    }
  }

  customElements.define(ELEMENT_NAME, InfiniteScrollGallery);
})();
